import { useEffect, useState } from 'react'
import translate from 'core/translate'
import moment from 'moment'
import helpers from 'core/helpers'
import helpersDisplays from 'core/helpers/_digitalSignages'
import { api } from 'core/api/ConnectionManager'
import { routes, changeLocation } from 'features/routes'
import { getURLSearchParamsString } from 'features/routes/utils'
import { DigitalSignage, IQueue } from './__updateQueue/UpdateQueue'
import { emitError } from 'features/appNotifications/AppNotifications.state'
import { useDispatch } from 'react-redux'

interface IState {
    startDate: string
    startTime: string
    updateNow: boolean
    selectedItems: any
    name: string
    updateTimeout: string
    maxDigitalSignageCount: string
    task: any
    editingMode: boolean
    types: {
        id: string
        name: string
    }[]
    platforms: any
    defaultPlatforms: string[]
    updateType: string
    selectedQueue: IQueue[]
    selectedTask: DigitalSignage | null
}

const defaultState = {
    startDate: moment().format(),
    startTime: moment().format('HH:mm:ss'),
    updateNow: false,
    selectedItems: [],
    task: null,
    editingMode: false,
    name: `${translate('newQueue')} ${moment().format('HH:mm:ss DD.MM.YYYY')}`,
    updateTimeout: '30',
    maxDigitalSignageCount: '10',
    updateType: 'client_app',
    selectedQueue: [],
    selectedTask: null,
}

const platformsForUpdate = [
    'sssp 2',
    'sssp 3',
    'sssp 4',
    'sssp 5',
    'sssp 6',
    'sssp 7',
    'sssp 10',
    'webos 2',
    'webos 3',
    'webos 3.2',
    'webos 4',
    'webos 4.1',
    'webos 6',
    'webos 9',
    'brightsign',
    'android',
    'tizen 7',
]

const UpdateDevicesMethods = (p_: { updateType: string }) => {
    const [state, setState] = useState<IState>({
        types: [
            {
                id: 'devicesForUpdate',
                name: translate('devicesForUpdate'),
            },
            {
                id: 'tasksForUpdate',
                name: translate('tasksForUpdate'),
            },
        ],
        platforms: [],
        defaultPlatforms: [],
        ...defaultState,
    })
    const dispatch = useDispatch()

    const timeOptions = helpers.getTimeOptions()

    const onSelectTask = (device: DigitalSignage) => {
        setState((prevState) => {
            return {
                ...prevState,
                selectedTask: device,
            }
        })
    }
    const onCancelSelect = () => {
        setState((prevState) => {
            return {
                ...prevState,
                selectedTask: null,
            }
        })
    }
    const onSelectAll = (queueList: IQueue[]) => {
        setState((prevState) => {
            return {
                ...prevState,
                selectedQueue: queueList,
            }
        })
    }
    const onQueueSelect = (queue: IQueue) => {
        setState((prevState) => {
            return {
                ...prevState,
                selectedQueue: prevState.selectedQueue.some((item) => item.id === queue.id)
                    ? prevState.selectedQueue.filter((item) => item.id !== queue.id)
                    : [...prevState.selectedQueue, queue],
            }
        })
    }

    useEffect(() => {
        getCompanyPlatforms()
    }, [])

    const getCompanyPlatforms = () => {
        api.send('getCompanyPlatforms', {}).then((res: any) => {
            let platforms: any = []

            res.forEach((platform: { name: string; updateType: string }) => {
                if (platformsForUpdate.includes(platform.name.toLowerCase())) {
                    platforms.push({
                        name: helpersDisplays.getPlatformName(platform.name),
                        //updateType: 'firmware',
                    })
                }
            })

            platforms = helpers.removeDuplicates(platforms, 'name')
            const defaultPlatforms = helpers.deepCopy(platforms)

            setState((prevState) => {
                return { ...prevState, platforms, defaultPlatforms }
            })
        })
    }
    const updateNowDevice = (updateNow: boolean) => {
        let startTime = moment().add(3, 'minutes').format('HH:mm:ss')

        if (!updateNow) {
            startTime = moment().format('HH:mm:ss')
        }

        setState((prevState) => {
            return { ...prevState, updateNow, startTime }
        })
    }

    const changeTab = (id: string) => {
        if (id !== p_.updateType) {
            setState((prevState) => {
                return { ...prevState, selectedItems: [] }
            })
        }
    }

    const convertData = (cb: any) => {
        const s_ = state
        const groupId: number[] = []
        const digitalSignageId: number[] = []

        s_.selectedItems.forEach((device: { id: number; type: string }) => {
            if (device.type === 'group') {
                groupId.push(device.id)
            } else {
                digitalSignageId.push(device.id)
            }
        })

        api.send('getDisplaysFromGroups', { digitalSignageId, groupId, fields: ['id', 'name', 'platform'] }).then(
            (devices: any) => {
                let data = devices.map((device: { id: number; platform: string }) => device.id)

                if (s_.platforms.length) {
                    data = devices.map((device: { id: number; platform: string }) => device.id)
                }

                cb(data)
            }
        )
    }

    const onUpdate = () => {
        const s_ = state
        const startDate = moment(s_.startDate).format('YYYY-MM-DD')

        let data: {
            id?: string
            updateType: string
            date: string
            digitalSignageId?: number[]
            name: string
            maxDigitalSignageCount: number
            updateTimeoutM: number
        } = {
            date: moment(`${startDate} ${s_.startTime}`).utc().toISOString(),
            updateType: s_.updateType,
            name: s_.name,
            maxDigitalSignageCount: +s_.maxDigitalSignageCount,
            updateTimeoutM: +s_.updateTimeout,
        }

        if (data.updateTimeoutM < 10) {
            dispatch(emitError(translate('minTimeoutWarning')))
            return
        }

        convertData((devices: number[]) => {
            data.digitalSignageId = devices

            const method = s_.task ? 'updateDsUpdateTask' : 'createDsUpdateTask'

            api.send(method, data).then(() => {
                setState((prevState) => {
                    return { ...prevState, ...defaultState, platforms: s_.defaultPlatforms }
                })
                changeLocation({
                    pathname: `/${routes.updateDevices.path}`,
                    search: getURLSearchParamsString({ updateType: 'tasksForUpdate' }),
                })
            })
        })
    }

    return {
        state,
        setState,
        changeTab,
        timeOptions,
        updateNowDevice,
        onUpdate,
        onQueueSelect,
        onSelectAll,
        onSelectTask,
        onCancelSelect,
    }
}

export default UpdateDevicesMethods
